"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useOptinMonsterReset = void 0;
var _react = require("react");
/* 
To be used in conjunction with optinMonsterScript. 

Ensures inline campaigns are injected into their holder div even if it becomes avaialble after 
initial page load (ex: loading spinner while content is fetched from cms), and that they are 
not duplicated by triggering a reset if the optinMonster script has already begun to show the campaign.

Example implementation in Site.jsx:

const currentEnv = process.env.REACT_APP_ENV;
const allowedEnvs = ['production', 'local'];

function Site() {
    const { pathname } = useLocation();
    const initializeMutationObservers = useOptinMonsterReset({ currentEnv, allowedEnvs });
    
    useEffect(() => {
        initializeMutationObservers();
    }, [initializeMutationObservers, pathname]);
*/

const useOptinMonsterReset = _ref => {
  let {
    currentEnv,
    allowedEnvs
  } = _ref;
  const contentObserverRef = (0, _react.useRef)(null);
  const campaignResetTimeoutRef = (0, _react.useRef)(null);
  const mainContentCheckTimeoutRef = (0, _react.useRef)(null);

  // Function to check and initialize observation on the main content
  const ensureMainContentObservation = (0, _react.useCallback)(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      configureMainContentObserver(mainContent);
    } else {
      mainContentCheckTimeoutRef.current = setTimeout(ensureMainContentObservation, 100); // Retry after 100ms
    }
  }, []);

  // Setup MutationObserver to check for new campaign holders
  const configureMainContentObserver = (0, _react.useCallback)(mainContent => {
    if (!contentObserverRef.current) {
      contentObserverRef.current = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "childList") {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // find holder div with id that starts with 'om-' and ends with '-holder'
                const holderDiv = node.querySelector('[id^="om-"][id$="-holder"]');
                if (holderDiv) {
                  clearTimeout(campaignResetTimeoutRef.current);
                  validateCampaignPresence(holderDiv);
                }
              }
            });
          }
        });
      });
    }
    contentObserverRef.current.observe(mainContent, {
      childList: true,
      subtree: true
    });
  }, []);

  // Validate Campaign presence to determine if reset is needed
  const validateCampaignPresence = (0, _react.useCallback)(holderDiv => {
    const campaignDiv = holderDiv.querySelector(".Campaign");
    let campaignShown = false;
    if (!campaignDiv) {
      // Campaign not found, setting up listener & campaign reset timeout
      const campaignShowListener = event => {
        const divId = holderDiv.id;
        const campaignId = getCampaignIdFromHolder(divId);
        if (event.detail.Campaign.id === campaignId) {
          // OptinMonster script has shown the campaign, no need to reset, as doing so could cause 
          // the campaign to get injected into the holder div again, causing a duplicate campaign
          document.removeEventListener("om.Campaign.show", campaignShowListener);
          campaignShown = true;
        }
      };
      document.addEventListener("om.Campaign.show", campaignShowListener);
      campaignResetTimeoutRef.current = setTimeout(() => {
        const campaignDiv = holderDiv.querySelector(".Campaign");
        if (!campaignShown && !campaignDiv && window.om2863_27375) {
          // Resetting OptinMonster to ensure campaign is shown
          window.om2863_27375.reset();
        }
        document.removeEventListener("om.Campaign.show", campaignShowListener);
      }, 1000);
    }
  }, []);

  // Extract campaign ID
  const getCampaignIdFromHolder = id => {
    const match = id.match(/^om-(.*?)-holder$/);
    return match ? match[1] : null;
  };

  // Effect to setup and cleanup
  (0, _react.useEffect)(() => {
    if (allowedEnvs.includes(currentEnv)) {
      ensureMainContentObservation();
    }
    return () => {
      var _contentObserverRef$c;
      (_contentObserverRef$c = contentObserverRef.current) === null || _contentObserverRef$c === void 0 || _contentObserverRef$c.disconnect();
      clearTimeout(campaignResetTimeoutRef.current);
      clearTimeout(mainContentCheckTimeoutRef.current);
    };
  }, [ensureMainContentObservation, currentEnv, allowedEnvs]);
  return ensureMainContentObservation;
};
exports.useOptinMonsterReset = useOptinMonsterReset;